import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleServerFormError, RateLimitError, trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { useCookie } from '#hooks/useCookie.ts';
import { REFERER_OPTIONS } from './referer-options.ts';
import { signupFormSchema, type SignupFormData } from './signup-schema.ts';
import { useSignupMutation } from './SignupForm.queries.ts';

const DEFAULT_VALUES = {
  email: '',
  referer: '',
} satisfies SignupFormData;

type Props = {
  onSuccessfulSubmit: () => void;
};

export function SignupForm({ onSuccessfulSubmit }: Props) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    resolver: zodResolver(signupFormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const signup = useSignupMutation();

  const [utmParamsStringified] = useCookie('utm_params');
  const utmParamsSchema = z.object({
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
  });
  const utmParams = useMemo(() => {
    try {
      const parsed = JSON.parse(utmParamsStringified);
      return utmParamsSchema.parse(parsed);
    } catch {
      return {};
    }
  }, [utmParamsSchema, utmParamsStringified]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const finalData = {
        ...data,
        referer: utmParams.utm_source === 'unplugged-podcast' ? 'unplugged-podcast' : data.referer,
      };
      await signup.mutateAsync(finalData);
      onSuccessfulSubmit();
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'email':
            case 'referer':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else if (error instanceof RateLimitError) {
        setError('root.global', { message: 'Too many requests, please try again later.' });
      } else {
        trackError(error);
        setError('root.global', { message: 'Could not sign up, please try again.' });
      }
    }
  });

  return (
    <form className="simple-page-content" method="post" onSubmit={onSubmit}>
      <Field id="email" label="What is your email address?" className="mb-24">
        <Input
          className="width-100"
          type="email"
          hasError={Boolean(errors.email)}
          enablePWManagers={true}
          {...register('email')}
        />
        {errors.email && <ErrorMessage className="mt-8">{errors.email.message}</ErrorMessage>}
      </Field>
      {utmParams.utm_source !== 'unplugged-podcast' ?
        <SelectControlled
          className="mb-24"
          label="How did you hear about us?"
          name="referer"
          control={control}
          placeholder="Please select..."
          hasError={Boolean(errors.referer)}
          errorMessage={errors.referer ? errors.referer.message : undefined}
          renderOptions={({ onChange }) => (
            <>
              {REFERER_OPTIONS.map(({ label, value }) => (
                <SelectItem key={value} value={value} onClick={() => onChange(value)}>
                  {label}
                </SelectItem>
              ))}
            </>
          )}
          renderValue={(value) => REFERER_OPTIONS.find((opt) => opt.value === value)?.label ?? null}
        />
      : null}
      <Button className="width-100 mb-24" disabled={isSubmitting} type="submit">
        Create account
      </Button>
      <p className="p-md mb-24">
        By creating an account you are agreeing to Defined Networking’s{' '}
        <a className="a" href="https://www.defined.net/terms/" target="_blank">
          Terms of Service
        </a>{' '}
        and{' '}
        <a className="a" href="https://www.defined.net/privacy/" target="_blank">
          Privacy Policy
        </a>
        .
      </p>
      {errors.root?.global?.message ?
        <ErrorMessage>{errors.root.global.message}</ErrorMessage>
      : null}
    </form>
  );
}
